import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import InputMask from 'react-input-mask';

import { InputProps, Popper, ValidationMessage } from '@xsolla/ui-core';
import { ChevronUpDown } from '@xsolla/ui-icons';

import { FlexParentBlock, SpaceBetweenBlock } from '../../../styled';
import { useFirstRender } from '../../../utils';
import { Icon } from '../icons/Icon';
import { IconName } from '../icons/types';
import countryData from './country-data';
import { PhoneOptions } from './PhoneOptions';
import {
  CountrySelectWrapper,
  DropdownIconWrapper,
  DropdownWrapper,
  FieldInputTitle,
  FullWideContentBlock,
  IconWrapper,
  PhoneInputWrapper,
  SeparatingLine,
  WrappedInput
} from './style';
import { parsePhone } from './utils';

type Props = InputProps & {
  label?: ReactNode;
  extraRight?: ReactNode;
  additional?: ReactNode;
  afterChange?: (value: string, country: IconName) => void;
  name: string;
};

const PhoneInput = ({
  label,
  extraRight,
  errorMessage,
  name,
  additional,
  readOnly,
  value,
  afterChange
}: Props): JSX.Element => {
  const [showHover, setShowHover] = useState<boolean>(false);
  const [currNumber, setCurrNumber] = useState<string>('');
  const [currCountry, setCurrCountry] = useState<IconName>('US');
  const firstRender = useFirstRender();

  useEffect(() => {
    if (value && typeof value === 'string') {
      const { country, number } = parsePhone(value);
      if (currCountry !== country) {
        setCurrCountry(country);
      }
      setCurrNumber(number);
    }
  }, [value]);

  useEffect(() => {
    if (!firstRender && afterChange) {
      afterChange(
        `+${countryData[currCountry].code}${currNumber.replaceAll(/[() \-_]/g, '')}`,
        currCountry
      );
    }
  }, [currCountry]);

  const leftPadding = useMemo(() => {
    return `${108 + countryData[currCountry].code.toString().length * 7}px`;
  }, [currCountry]);

  const selectCountry = useCallback((country: IconName) => {
    setCurrCountry(country);
  }, []);

  const countryWrapper = useMemo(() => {
    return (
      <Popper stretched entry={<PhoneOptions setCountry={selectCountry} />}>
        {(referenceProps) => (
          <CountrySelectWrapper
            readOnly={!!readOnly}
            {...referenceProps}
            onClick={(e) => {
              if (!readOnly) {
                referenceProps.onClick(e);
              }
            }}
            onMouseEnter={() => setShowHover(true)}
            onMouseLeave={() => setShowHover(false)}>
            <IconWrapper>
              <Icon name={currCountry} />
            </IconWrapper>
            +{countryData[currCountry].code}
            <DropdownIconWrapper>
              <ChevronUpDown />
            </DropdownIconWrapper>
            <SeparatingLine />
          </CountrySelectWrapper>
        )}
      </Popper>
    );
  }, [currCountry, readOnly]);

  return (
    <>
      <SpaceBetweenBlock>
        <FieldInputTitle>{label}</FieldInputTitle>
        {additional && <div>{additional}</div>}
      </SpaceBetweenBlock>
      <FlexParentBlock>
        <FullWideContentBlock>
          <PhoneInputWrapper>
            <InputMask
              mask={countryData[currCountry].mask}
              disabled={readOnly}
              name={name}
              spellCheck={false}
              onChange={(e) => {
                if (afterChange) {
                  afterChange(
                    `+${countryData[currCountry].code}${e.target.value.replaceAll(
                      /[() \-_]/g,
                      ''
                    )}`,
                    currCountry
                  );
                }
              }}
              value={currNumber}>
              {(inputProps: unknown) => (
                <WrappedInput
                  leftPadding={leftPadding}
                  hovered={showHover}
                  {...inputProps}
                  disabled={readOnly}
                />
              )}
            </InputMask>
            <DropdownWrapper>{countryWrapper}</DropdownWrapper>
          </PhoneInputWrapper>
          <ValidationMessage>{errorMessage}</ValidationMessage>
        </FullWideContentBlock>
        {extraRight}
      </FlexParentBlock>
    </>
  );
};

export default PhoneInput;
